import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  FormOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import {Card, Modal, Timeline as _Timeline, Typography} from 'antd';
import {Document, DocumentEvent, Maybe, useDocumentEventsQuery} from 'api/generated';
import {AnchorLink, EmailLink} from 'components';
import {Code} from 'components/Code';
import {colors} from 'lib/colors';
import {formatDate} from 'lib/formaters';
import {prettifyEvent} from 'lib/utils';
import React, {useState} from 'react';
import styled from 'styled-components';
import {EventType} from 'types';

const {Paragraph} = Typography;

type Props = {
  document: Document;
};

const Timeline = styled(_Timeline)`
  margin: 10px 0 0 3px !important;
  & .ant-timeline-item-last {
    padding-bottom: 0;
    & > .ant-timeline-item-content {
      min-height: auto;
    }
  }
`;

const eventMapping: Record<
  string,
  {title: string | JSX.Element; icon?: any; color?: string; theme?: 'filled'}
> = {
  [EventType.DOCUMENT_APPROVAL_REQUIRED]: {
    title: 'Document approval requested',
    icon: ClockCircleOutlined,
    color: colors.secondary
  },
  [EventType.DOCUMENT_PENDING]: {
    title: 'Document created',
    icon: InfoCircleOutlined,
    color: colors.secondary
  },
  [EventType.DOCUMENT_PROCESSING]: {
    title: 'Document is processing',
    icon: ClockCircleOutlined,
    color: colors.secondary
  },
  [EventType.DOCUMENT_APPROVED]: {
    title: 'Document approved',
    icon: CheckCircleFilled,
    color: colors.green
  },
  [EventType.DOCUMENT_REJECTED]: {
    title: 'Document rejected',
    icon: CloseCircleOutlined,
    color: colors.red
  },
  [EventType.DOCUMENT_NOTE_UPDATED]: {
    title: 'Note has been updated',
    icon: FormOutlined,
    color: colors.secondary
  },
  [EventType.DOCUMENT_EXPIRED]: {
    title: 'Document expired',
    icon: CloseCircleOutlined,
    color: colors.secondary
  }
};

const getDocumentMessage = (event: DocumentEvent) => {
  const doc = event.object;
  if (
    [EventType.DOCUMENT_REJECTED, EventType.DOCUMENT_APPROVAL_REQUIRED].includes(
      event.type as EventType
    )
  ) {
    if (doc.note) {
      return [doc.statusMessage, doc.note].join('\n');
    }
    return doc.statusMessage;
  }

  if (event.type === EventType.DOCUMENT_NOTE_UPDATED) {
    return doc.note;
  }
};

export const getEventItem = (event: DocumentEvent) => {
  return (
    eventMapping[event.type] || {
      title: 'Unknown event',
      icon: InfoCircleOutlined,
      color: colors.secondary
    }
  );
};

const getTitle = (event: DocumentEvent) => {
  const item = getEventItem(event);
  if (!item) return <></>;
  const details = event.object.traceDetails;
  if (details?.userEmail) {
    return (
      <>
        {item.title} by <EmailLink email={details.userEmail}>{details.userName}</EmailLink>
      </>
    );
  } else if (details?.source) {
    return (
      <>
        {item.title} ({details.source})
      </>
    );
  }
  return item.title;
};

export const DocumentTimeline: React.FC<Props> = ({document}) => {
  const [selectedEvent, setSelectedEvent] = useState<Maybe<DocumentEvent>>();
  const [isInfoOpen, setInfoOpen] = useState(false);
  const {data, loading} = useDocumentEventsQuery({
    skip: !document,
    variables: {documentId: document?.id}
  });
  const events = data?.documentEvents.items ?? [];

  const handleInfoClose = () => {
    setInfoOpen(false);
  };

  return (
    <>
      <Card title="Timeline" size="small" style={{marginBottom: 20}} loading={loading || !document}>
        <Timeline>
          {events.map((event, i) => {
            const item = getEventItem(event);
            return (
              <Timeline.Item key={i} dot={<item.icon style={{color: item.color}} />}>
                <div>
                  {getTitle(event)}{' '}
                  <AnchorLink
                    onClick={() => {
                      setSelectedEvent(event);
                      setInfoOpen(true);
                    }}>
                    <EyeOutlined />
                  </AnchorLink>
                </div>
                <Paragraph type="secondary" style={{whiteSpace: 'pre-line', marginBottom: 0}}>
                  {getDocumentMessage(event)}
                </Paragraph>
                <Paragraph type="secondary" style={{fontSize: '12px', marginBottom: 0}}>
                  {formatDate(event.createdAt, 'X', true, true)}
                </Paragraph>
              </Timeline.Item>
            );
          })}
        </Timeline>
      </Card>
      <Modal
        title="Event data"
        width={800}
        open={isInfoOpen}
        footer={false}
        onCancel={handleInfoClose}>
        {selectedEvent && <Code>{prettifyEvent(selectedEvent)}</Code>}
      </Modal>
    </>
  );
};
